import {Injectable} from '@angular/core';
import {CanActivate, Router, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {NGXLogger} from 'ngx-logger';
import {canChangePassword, LicenceService} from '@app/core/services/licence.service';

/**
 * Blocks the password route for sessions which must not change the main account's password.
 *
 * Background: a CoAccount shares the customerId of the main account, and a token login from
 * app/device carries no login identity at all — in both cases a password change would have hit the
 * main account. The API denies this now, this guard keeps the user from running into the error:
 * hiding the button in /mydata is not enough, the route is reachable directly via URL.
 *
 * See .claude/notes_coaccount_identity.md.
 */
@Injectable({
  providedIn: 'root'
})
export class CoAccountGuard implements CanActivate {

  constructor(private licence: LicenceService, private router: Router, private log: NGXLogger) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.licence.getLoginEmail().pipe(
      take(1),
      map(info => {
        if (!canChangePassword(info)) {
          this.log.warn('Session must not change the account password, route is not allowed');
          return this.router.parseUrl('/mydata');
        }
        return true;
      }),
      /*
       * Allow on error: an API which does not answer here also breaks /mydata itself, and an API
       * which does not know the field yet (older backend) must not lock out regular customers.
       */
      catchError(() => of(true))
    );
  }

}
